/**
 * Professional Search Hook
 * Feature: 004-restore-missing-features
 * Task: T041
 *
 * Client-side filtering hook for professionals by name or email
 */

import { useMemo } from 'react'

interface Professional {
  id: string
  profile_id: string
  role: 'admin' | 'professional' | 'assistant'
  is_active: boolean
  joined_at: string
  profiles: {
    id: string
    full_name: string
    email: string
    phone: string | null
  }
}

interface UseProfessionalSearchOptions {
  professionals: Professional[] | undefined
  searchTerm: string
}

interface UseProfessionalSearchResult {
  filteredProfessionals: Professional[]
  hasSearchTerm: boolean
  resultCount: number
}

/**
 * Filters professionals by name or email using case-insensitive matching
 */
export function useProfessionalSearch({
  professionals,
  searchTerm,
}: UseProfessionalSearchOptions): UseProfessionalSearchResult {
  const filteredProfessionals = useMemo(() => {
    if (!professionals) return []
    if (!searchTerm.trim()) return professionals

    const normalizedSearch = searchTerm.toLowerCase().trim()

    return professionals.filter((professional) => {
      const name = professional.profiles?.full_name?.toLowerCase() ?? ''
      const email = professional.profiles?.email?.toLowerCase() ?? ''

      return name.includes(normalizedSearch) || email.includes(normalizedSearch)
    })
  }, [professionals, searchTerm])

  return {
    filteredProfessionals,
    hasSearchTerm: searchTerm.trim().length > 0,
    resultCount: filteredProfessionals.length,
  }
}
